import React, { useState, useEffect } from 'react';
import { Button } from '../../ui/Button';
import { apiGet } from '../../../api/client';
import { type RestoreSummary } from '../../../api/backup';
import { toast } from 'sonner';

interface RestoreSummaryStepProps {
  onComplete: () => void;
}

export function RestoreSummaryStep({ onComplete }: RestoreSummaryStepProps) {
  const [summary, setSummary] = useState<RestoreSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiGet<RestoreSummary>('/admin/backup/restore-summary')
      .then(setSummary)
      .catch(() => toast.error('Errore nel caricamento riepilogo'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <p style={{ textAlign: 'center', padding: '2rem' }}>Caricamento riepilogo...</p>;
  }

  if (!summary) {
    return <p style={{ textAlign: 'center', padding: '2rem' }}>Nessun dato importato.</p>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
        I seguenti dati sono stati importati. Puoi modificarli prima di completare il setup.
      </p>

      {summary.studio && (
        <div style={{ background: 'var(--glass-bg-card)', borderRadius: 'var(--radius-card)', padding: '0.75rem' }}>
          <h4 style={{ fontSize: 'var(--text-sm)', fontWeight: 600, marginBottom: '0.5rem' }}>Profilo Studio</h4>
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
            {(summary.studio as any).studioName ?? '—'} · {(summary.studio as any).ownerCity ?? '—'}
          </p>
        </div>
      )}

      {summary.invoice && (
        <div style={{ background: 'var(--glass-bg-card)', borderRadius: 'var(--radius-card)', padding: '0.75rem' }}>
          <h4 style={{ fontSize: 'var(--text-sm)', fontWeight: 600, marginBottom: '0.5rem' }}>Fatturazione</h4>
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
            P.IVA: {(summary.invoice as any).ownerVatNumber ?? '—'} · IBAN: {(summary.invoice as any).ownerIban ?? '—'}
          </p>
        </div>
      )}

      {summary.finance && (
        <div style={{ background: 'var(--glass-bg-card)', borderRadius: 'var(--radius-card)', padding: '0.75rem' }}>
          <h4 style={{ fontSize: 'var(--text-sm)', fontWeight: 600, marginBottom: '0.5rem' }}>Tariffe e Fisco</h4>
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
            Compenso: €{(summary.finance as any).ownerDailyRate ?? '—'} · Regime: {(summary.finance as any).defaultTaxMode ?? '—'}
          </p>
        </div>
      )}

      <div style={{ background: 'var(--glass-bg-card)', borderRadius: 'var(--radius-card)', padding: '0.75rem' }}>
        <h4 style={{ fontSize: 'var(--text-sm)', fontWeight: 600, marginBottom: '0.5rem' }}>Record Importati</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.25rem', fontSize: 'var(--text-xs)' }}>
          <span>Clienti: {summary.counts.clients}</span>
          <span>Collaboratori: {summary.counts.collaborators}</span>
          <span>Eventi: {summary.counts.events}</span>
          <span>Servizi: {summary.counts.productTemplates}</span>
        </div>
      </div>

      <Button onClick={onComplete}>
        Applica e completa setup
      </Button>
    </div>
  );
}
